import React, { useState, useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useMediaQuery } from "@mui/material";
import Summary from "./summary";
import Assets from "./accounts";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { oneBranchHasPermission } from "src/utils";
import { OCPermissions } from "src/constants/permissions";
import NoAccessPage from "src/pages/no-access";
import TransactionList from "src/pages/operation-cost/tabs/transaction-list";
import AssetsCategoriesTable from "./categories";

interface TabProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }} height={"40px"}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const OperationCostTabs = () => {
  const { t } = useTranslation("operationCost");
  const { t: generalT } = useTranslation("general");
  const { t: inventoryT } = useTranslation("inventory");
  const navigate = useNavigate();
  const location = useLocation();

  const isSmallScreen = useMediaQuery("(max-width:700px)");
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: moment().startOf("month").format("yyyy-MM-DD"),
    endDate: moment().endOf("month").format("yyyy-MM-DD"),
  });

  // permissions
  const viewOCSummary = oneBranchHasPermission(OCPermissions.viewOCSummary);
  const viewOCList = oneBranchHasPermission(OCPermissions.viewOCList);
  const viewOCAccounts = oneBranchHasPermission(OCPermissions.viewOCAccounts);

  // Map paths to tab indices
  const tabPaths = [
    "/assets/summary",
    "/assets/category",
    "/assets/list",
    "/assets/cost-list",
  ];
  const currentPath = location.pathname;

  // Find the tab index based on the current URL path
  const currentTabIndex = tabPaths.indexOf(currentPath);
  const [value, setValue] = useState(
    currentTabIndex === -1 ? 0 : currentTabIndex,
  );

  const labels = [
    t("summary"),
    inventoryT("categories"),
    generalT("fixedAssets"),
    t("fixedAssetsBills"),
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(tabPaths[newValue]); // Change the URL based on the selected tab
  };

  // Update the tab value when the URL changes
  useEffect(() => {
    const newTabIndex = tabPaths.indexOf(currentPath);
    if (newTabIndex !== value) {
      setValue(newTabIndex === -1 ? 0 : newTabIndex);
    }
  }, [currentPath]);

  return (
    <Box>
      <Box
        bgcolor={"#F9FAFB"}
        p={0.5}
        border={"1px solid  #EAECF0"}
        borderRadius={1}
        width={{ xs: "100%", md: "fit-content" }}
        display={"flex"}
        justifyContent={isSmallScreen ? "center" : ""}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          TabIndicatorProps={{ style: { display: "none" } }}
          orientation={isSmallScreen ? "vertical" : "horizontal"}
          sx={{
            minHeight: { xs: "100%", md: "35px" },
            height: { xs: "100%", md: "35px" },
          }}
        >
          {labels.map((label, index) => (
            <Tab
              key={index}
              disableRipple
              sx={{
                borderRadius: "6px",
                color: "#667085",
                height: "33px",
                minHeight: "33px",
                "&.Mui-selected": {
                  bgcolor: "#FFFFFF",
                  color: "#344054",
                  boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
                  m: "1px",
                },
              }}
              label={label}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>

      <CustomTabPanel value={value} index={0}>
        {viewOCSummary ? (
          <Summary dateRange={dateRange} setDateRange={setDateRange} />
        ) : (
          <NoAccessPage />
        )}
      </CustomTabPanel>

      <CustomTabPanel value={value} index={1}>
        {true ? <AssetsCategoriesTable /> : <NoAccessPage />}
      </CustomTabPanel>

      <CustomTabPanel value={value} index={2}>
        {viewOCAccounts ? <Assets /> : <NoAccessPage />}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        {viewOCList ? <TransactionList isAssetsOnly /> : <NoAccessPage />}
      </CustomTabPanel>
    </Box>
  );
};

export default OperationCostTabs;
